/**
 * Auth Layout
 *
 * Split-screen layout for authentication pages.
 * Left panel: Login form with branding
 * Right panel: Full-bleed industrial imagery with overlay
 */

import { Outlet } from 'react-router-dom';
import logo from '../assets/images/logo.png';

export function AuthLayout() {
  return (
    <div className="flex min-h-screen">
      {/* Left Panel - Auth Form */}
      <div className="relative w-full lg:w-[38%] lg:min-w-[400px] lg:max-w-[500px] flex flex-col bg-white/95 backdrop-blur-sm lg:bg-white lg:backdrop-blur-none z-10">
        {/* Subtle background pattern */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2300674a' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Form container - centered vertically */}
        <div className="relative flex-1 flex flex-col justify-center items-center px-10 py-12">
          <div className="w-full max-w-[340px]">
            {/* Logo - centered above form */}
            <div className="flex justify-center mb-8">
              <img
                src={logo}
                alt="Guild Insights"
                className="h-24 w-auto object-contain"
              />
            </div>
            <Outlet />
          </div>
        </div>

        {/* Footer */}
        <div className="relative px-10 pb-8">
          <p className="text-xs text-gray-400">
            © {new Date().getFullYear()} Guild Insights. All rights reserved.
          </p>
        </div>

        {/* Decorative corner accent */}
        <div className="absolute bottom-0 right-0 w-32 h-32 overflow-hidden pointer-events-none">
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-gradient-to-tl from-guild-mint/20 to-transparent rounded-full translate-x-1/2 translate-y-1/2" />
        </div>
      </div>

      {/* Right Panel - Background Image (always visible) */}
      <div className="fixed inset-0 lg:relative lg:flex-1 overflow-hidden">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-right"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1581091212991-8891c7d4bd9b?q=80&w=2070&auto=format&fit=crop')`,
          }}
        />

        {/* Gradient overlays for depth */}
        <div className="absolute inset-0 bg-gradient-to-r from-guild-green/40 via-transparent to-guild-green/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/10" />

        {/* Feature callout - now at top-left */}
        <div className="hidden lg:block absolute top-8 left-8">
          <div className="max-w-lg">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
              <div className="w-2 h-2 rounded-full bg-guild-mint animate-pulse" />
              <span className="text-xs font-semibold text-white/90 uppercase tracking-wider">
                Real-time Performance Insights
              </span>
            </div>
          </div>
        </div>

        {/* Decorative elements - hidden on mobile */}
        <div className="hidden lg:block absolute inset-0 pointer-events-none">
          {/* Bottom-left corner accent */}
          <div className="absolute bottom-8 left-8">
            <div className="w-16 h-16 border-l-2 border-b-2 border-white/30 rounded-bl-xl" />
          </div>

          {/* Top-right corner accent */}
          <div className="absolute top-8 right-8">
            <div className="w-16 h-16 border-r-2 border-t-2 border-white/30 rounded-tr-xl" />
          </div>
        </div>
      </div>
    </div>
  );
}
